<template>
  <section>
    <b-modal
      id="modal-create-activity"
      ref="modal-create-activity"
      data-cy="activity-modal"
      :data-view-ready="!loading"
      :modal-class="'has-confirmation-modal'"
      no-close-on-backdrop>
      <template #modal-header>
        <div class="w-100">
          <h5
            v-if="activityDetail"
            class="mb-0 d-inline normal modal-title">
            {{ $t('message.contact.activity.edit') }}
          </h5>
          <h5
            v-else
            class="mb-0 d-inline modal-title">
            {{ $t('message.contact.activity.create') }}
          </h5>
          <coozzy-button
            class="float-right"
            design="transparent"
            data-cy="activity-modal-close-button"
            @click="hide()">
            <img
              alt="Placeholder"
              class="close-img"
              src="@/assets/icon_close.svg">
          </coozzy-button>
          <coozzy-button
            v-if="!isEditable"
            class="float-right"
            design="transparent"
            @click="isEditable = true">
            <coozzy-edit-icon />
          </coozzy-button>
          <coozzy-button
            v-if="canDeleteActivity && !isEditable"
            class="float-right"
            design="transparent"
            @click="deleteActivityClicked">
            <coozzy-delete-icon />
          </coozzy-button>
        </div>
      </template>
      <div
        v-if="loading"
        class="row">
        <div class="col text-center">
          <coozzy-spinner class="m-3" />
        </div>
      </div>
      <div
        v-else
        class="row">
        <div class="col-sm-12 col-md-12 mb-2">
          <coozzy-form-input
            v-if="isEditable"
            v-model="activity.subject"
            :name="$t('message.calendar.reminder.subject')+ '*'"
            :state="$v.activity.subject?.$dirty && $v.activity.subject?.$error ? false : null" />
          <template v-else>
            <label>{{ $t('message.calendar.reminder.subject') }}</label>
            <p data-cy="activity-modal-subject-value">
              {{ activity.subject }}
            </p>
          </template>
        </div>
        <div class="col-sm-12 col-md-12 mb-2">
          <label> {{ $t('message.onBoarding.buildings.objects.renovation.text') }} </label>
          <coozzy-form-textarea
            v-if="isEditable"
            v-model="activity.text"
            :initial="activity.text"
            :rows="3"
            :show-label="false"
            :value-text-area="activity.text">
            {{ activity.text }}
          </coozzy-form-textarea>
          <p
            v-else
            data-cy="activity-modal-text-value">
            {{ activity.text }}
          </p>
        </div>
        <div class="col-6 mb-2">
          <label>{{ $t('message.contact.activity.source') }}</label>
          <coozzy-form-select
            v-if="isEditable"
            id="source"
            v-model="activity.source">
            <option
              value="ACTIVITY_SOURCE_UNDEFINED">
              -
            </option>
            <option
              value="ACTIVITY_SOURCE_PHONE">
              {{ $t('message.contact.activity.sourceType.ACTIVITY_SOURCE_PHONE') }}
            </option>
            <option
              value="ACTIVITY_SOURCE_MAIL">
              {{ $t('message.contact.activity.sourceType.ACTIVITY_SOURCE_MAIL') }}
            </option>
            <option
              value="ACTIVITY_SOURCE_PERSONAL">
              {{ $t('message.contact.activity.sourceType.ACTIVITY_SOURCE_PERSONAL') }}
            </option>
          </coozzy-form-select>
          <p
            v-else
            data-cy="activity-modal-source-value"
            class="show-data">
            <span v-if="activity.source === 'ACTIVITY_SOURCE_PHONE'">{{
              $t('message.contact.activity.sourceType.ACTIVITY_SOURCE_PHONE')
            }}</span>
            <span v-else-if="activity.source === 'ACTIVITY_SOURCE_MAIL'">{{
              $t('message.contact.activity.sourceType.ACTIVITY_SOURCE_MAIL')
            }}</span>
            <span v-else-if="activity.source === 'ACTIVITY_SOURCE_PERSONAL'">{{
              $t('message.contact.activity.sourceType.ACTIVITY_SOURCE_PERSONAL')
            }}</span>
            <span v-else>-</span>
          </p>
        </div>
        <div class="col-6 mb-2">
          <label>{{ $t('message.calendar.event.date') }}*</label>
          <coozzy-form-input
            v-if="isEditable"
            id="startDate"
            ref="startDate"
            v-model="time.date"
            :state="$v.activity.timestampStart?.$dirty && $v.activity.timestampStart?.$error ? false : null"
            type="date"
            @blur="updateTimestamp" />
          <template v-else>
            <p data-cy="activity-modal-date-value">
              {{ time.date || '-' }}
            </p>
          </template>
        </div>
        <div class="col-4 mb-2">
          <label>{{ $t('message.contact.activity.start') }}{{ activity.billable ? '*' : '' }}</label>
          <coozzy-form-select
            v-if="isEditable"
            id="activity-start-time"
            v-model="time.start"
            :class="{ 'is-invalid': $v.time.start.$dirty && $v.time.start.$error }"
            @input="updateTimestamp">
            <option
              value=""
              disabled
              hidden>
              --:--
            </option>
            <option
              v-for="t in timeOptions"
              :key="'start' + t"
              :value="t">
              {{ t }}
            </option>
          </coozzy-form-select>
          <template v-else>
            <p data-cy="activity-modal-start-value">
              {{ activity.hasStartTime ? time.start : '-' }}
            </p>
          </template>
        </div>
        <div class="col-4 mb-2">
          <label>{{ $t('message.contact.activity.end') }}{{ activity.billable ? '*' : '' }}</label>
          <coozzy-form-select
            v-if="isEditable"
            id="activity-end-time"
            v-model="time.end"
            :class="{ 'is-invalid': $v.time.end.$dirty && $v.time.end.$error }"
            @input="updateTimestamp">
            <option
              value=""
              disabled
              hidden>
              --:--
            </option>
            <option
              v-for="t in timeOptions"
              :key="'end' + t"
              :value="t">
              {{ t }}
            </option>
          </coozzy-form-select>
          <template v-else>
            <p data-cy="activity-modal-end-value">
              {{ activity.hasEndTime ? time.end : '-' }}
            </p>
          </template>
        </div>
        <div class="col-4 mb-2">
          <label>{{ $t('message.contact.activity.duration') }}</label>
          <p
            data-cy="activity-modal-duration-value"
            class="show-data mb-0">
            {{ duration }}
          </p>
        </div>
        <div class="col-sm-12 col-md-6 mb-2 d-flex align-items-center">
          <coozzy-form-checkbox
            v-if="isEditable"
            id="activity-billable"
            :initial="activity.billable"
            class="p-0"
            @change="onBillableChange">
            {{ $t('message.tableColumnHeadings.billable') }}
          </coozzy-form-checkbox>
          <template v-else>
            <div>
              <label>{{ $t('message.tableColumnHeadings.billable') }}</label>
              <p class="show-data mb-0">
                <coozzy-check-mark-icon v-if="activity.billable" />
                <span v-else>-</span>
              </p>
            </div>
          </template>
        </div>
        <div class="col-sm-12 col-md-6 mb-2">
          <coozzy-form-input-numeric
            v-if="isEditable && activity.billable"
            id="activity-hourly-rate"
            v-model="activity.hourlyRate"
            :is-decimal="true"
            filter="formatPrice"
            min="0"
            placeholder="CHF"
            :check-valide="$v.activity.hourlyRate.$dirty && $v.activity.hourlyRate.$error ? 'is-invalid' : ''"
            :name="$tc('message.employeesSettings.hourlyRate') + '*'"
            @input="userOverrodeHourlyRate = true" />
          <div
            v-else-if="isEditable"
            class="activity-hourly-rate-placeholder"
            aria-hidden="true">
            <label class="mb-0">{{ $tc('message.employeesSettings.hourlyRate') }}*</label>
            <div class="activity-hourly-rate-placeholder-input" />
          </div>
          <template v-else-if="activity.billable">
            <label>{{ $t('message.employeesSettings.hourlyRate') }}</label>
            <p class="show-data mb-0">
              {{ activity.hourlyRate != null ? $options.filters.formatPrice(activity.hourlyRate) : '-' }}
            </p>
          </template>
        </div>
        <div
          id="list-connections"
          data-cy="activity-modal-connections"
          class="col-sm-12 col-md-12 mb-2">
          <label>{{ $t('message.ticketOverview.ticket.connections') }}</label>
          <div
            v-for="(ref, index) in references"
            :key="index"
            class="d-flex align-items-center">
            <coozzy-button
              v-if="isEditable"
              class="border mb-2"
              design="transparent"
              size="small"
              @click="removeReference(index)">
              <coozzy-delete-icon />
            </coozzy-button>
            <div class="containerCircles">
              <div
                v-if="ref.tenancy"
                v-b-tooltip="{container: '#list-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.tenancy')}"
                class="color-box mt-0 ml-2 mr-2 tenancy" />
              <div
                v-if="ref.condominium"
                v-b-tooltip="{container: '#list-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.condo_owner')}"
                class="color-box mt-0 ml-2 mr-2 condo_owner" />
              <div
                v-if="ref.object && ref.object.condominiumOwnership === true && !ref.tenancy && !ref.condominium && !ref.device"
                v-b-tooltip="{container: '#list-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.condominium')}"
                class="color-box mt-0 ml-2 mr-2 condominium" />
              <div
                v-if="ref.object && ref.object.condominiumOwnership === false && !ref.tenancy && !ref.condominium && !ref.device"
                v-b-tooltip="{container: '#list-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.object')}"
                class="color-box mt-0 ml-2 mr-2 object" />
              <div
                v-if="ref.device"
                v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.device')}"
                class="color-box mt-0 ml-2 mr-2 device" />
              <div
                v-if="ref.building && !ref.object && !ref.device"
                v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.building')}"
                class="color-box mt-0 ml-2 mr-2 not_activated" />
              <div
                v-if="ref.property && !ref.building && !ref.device"
                v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.property')}"
                class="color-box mt-0 ml-2 mr-2 current" />
              <div
                v-if="ref.contact"
                v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.contact')}"
                class="color-box mt-0 ml-2 mr-2 terminated" />
              <div
                v-if="ref.request"
                v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.request')}"
                class="color-box mt-0 ml-2 mr-2 request" />
            </div>
            <span v-if="ref.request">
              <router-link
                :to="redirectToRequestDetail(ref.request.objectId, ref.request.id)">
                <span
                  v-for="(contact, j) in ref.listContact"
                  :key="j">
                  <span v-if="j > 0">- </span>{{ contact.name }}
                </span>
              </router-link>
            </span>
            <span v-if="ref.property">
              <router-link
                v-if="!isMarketingModule"
                :to="redirectToPropertyDetail(ref.property.objectID || ref.property.id)">
                {{ ref.property.name }}
              </router-link>
              <span v-else>{{ ref.property.name }}</span>
            </span>
            <span v-if="ref.building">
              <span v-if="ref.property"> > </span>
              <router-link
                v-if="!isMarketingModule"
                :to="redirectToBuildingDetail(ref.building.objectID || ref.building.id)">
                {{ ref.building.name }}
              </router-link>
              <span v-else>{{ ref.building.name }}</span>
            </span>
            <span v-if="ref.object">
              <span v-if="ref.building"> > </span>
              <router-link
                v-if="!isMarketingModule"
                :to="redirectToDetail(ref.object.buildingId, ref.object.objectID || ref.object.id, 'objects')">
                {{
                  ref.object.name !== '' ? ref.object.name : ref.object.number !== '' ? ref.object.number : ref.object.numericId
                }}
              </router-link>
              <span v-else> {{
                ref.object.name !== '' ? ref.object.name : ref.object.number !== '' ? ref.object.number : ref.object.numericId
              }}</span>
            </span>
            <span v-if="ref.device">
              <span v-if="ref.object || ref.building || ref.property"> > </span>
              <router-link
                v-if="redirectToDeviceDetail(ref.device.referenceId, ref.device.objectID || ref.device.id) && !isMarketingModule"
                :to="redirectToDeviceDetail(ref.device.referenceId, ref.device.objectID || ref.device.id)">
                <template v-if="ref.device.type && ref.device.type !== 'DEVICE_TYPE_UNDEFINED'">
                  {{
                    $t('message.onBoarding.devices.typeValues.' + ref.device.type).length > 50 ? $t('message.onBoarding.devices.typeValues.' + ref.device.type).slice(0, 50) + '...' : $t('message.onBoarding.devices.typeValues.' + ref.device.type)
                  }}
                </template>
                <template v-else>
                  -
                </template>
              </router-link>
              <span v-else>
                <template v-if="ref.device.type && ref.device.type !== 'DEVICE_TYPE_UNDEFINED'">
                  {{
                    $t('message.onBoarding.devices.typeValues.' + ref.device.type).length > 50 ? $t('message.onBoarding.devices.typeValues.' + ref.device.type).slice(0, 50) + '...' : $t('message.onBoarding.devices.typeValues.' + ref.device.type)
                  }}
                </template>
                <template v-else>
                  -
                </template>
              </span>
            </span>
            <span v-if="ref.object && ref.tenancy">
              <span v-if="ref.object"> > </span>
              <router-link
                v-if="!isMarketingModule"
                :to="redirectToDetail(ref.object.buildingId, ref.tenancy.objectID || ref.tenancy.id, 'tenants')">
                <template v-if="ref.tenancy.tenants">
                  <span
                    v-for="(contact, j) in ref.tenancy.tenants"
                    :key="j">
                    <span v-if="j > 0">- </span>{{ contact.name }}
                  </span>
                </template>
                <template v-else>
                  <span
                    v-for="(contact, j) in ref.listContact"
                    :key="j">
                    <span v-if="j > 0">- </span>{{ contact.name }}
                  </span>
                </template>
              </router-link>
              <span v-else>
                <template v-if="ref.tenancy.tenants">
                  <span
                    v-for="(contactTen, j) in ref.tenancy.tenants"
                    :key="j">
                    <span v-if="j > 0">- </span>{{ contactTen.name }}
                  </span>
                </template>
                <template v-else>
                  <span
                    v-for="(contact, j) in ref.listContact"
                    :key="j">
                    <span v-if="j > 0">- </span>{{ contact.name }}
                  </span>
                </template>
              </span>
            </span>
            <span v-if="ref.object && ref.condominium">
              <span v-if="ref.object"> > </span>
              <router-link
                v-if="!isMarketingModule"
                :to="redirectToDetail(ref.object.buildingId, ref.condominium.objectID || ref.condominium.id, 'condominium')">
                <template v-if="ref.condominium.condominiumOwners">
                  <span
                    v-for="(contact, j) in ref.condominium.condominiumOwners"
                    :key="j">
                    {{ contact.name }}
                  </span>
                </template>
                <template v-else>
                  <span
                    v-for="(contact, j) in ref.listContact"
                    :key="j">
                    {{ contact.name }}
                  </span>
                </template>
              </router-link>
              <span v-else>
                <template v-if="ref.condominium.condominiumOwners">
                  <span
                    v-for="(contact, j) in ref.condominium.condominiumOwners"
                    :key="j">
                    {{ contact.name }}
                  </span>
                </template>
                <template v-else>
                  <span
                    v-for="(contact, j) in ref.listContact"
                    :key="j">
                    {{ contact.name }}
                  </span>
                </template>
              </span>
            </span>
            <span v-if="ref.contact">
              <router-link
                :to="redirectToContactDetail(ref.contact.objectID || ref.contact.id)">
                {{ ref.contact.name }}
              </router-link>
            </span>
          </div>
          <div
            v-if="isEditable"
            class="row">
            <div class="col-3">
              <coozzy-form-select
                v-model="typeOfConnection">
                <option
                  :class="typeOfConnection === null ? 'selectedOption' : ''"
                  :value="null">
                  {{ $t('message.search.filter').toLowerCase() }}
                </option>
                <option
                  v-for="item in availableConnectionTypes"
                  :key="item.key"
                  :class="typeOfConnection?.key === item.key ? 'selectedOption' : ''"
                  :value="item">
                  {{ $t('message.ticketOverview.ticket.connection_items.' + item.key) }}
                </option>
              </coozzy-form-select>
            </div>
            <div class="col-9">
              <contact-search-provider
                :filters="connectionContactFilters"
                :disabled="!includesContacts"
                class="w-100"
                silent>
                <template #default="{ hits: contactHits, refine: refineContacts }">
                  <ais-instant-search
                    v-if="isEditable && searchClient"
                    :index-name="algoliaIndexName"
                    :routing="routing"
                    :search-client="searchClient"
                    class="w-100">
                    <!-- eslint-disable vue/attribute-hyphenation -->
                    <ais-configure
                      :filters="query"
                      :hitsPerPage="10" />
                    <template v-if="typeOfConnection === null">
                      <ais-index index-name="building" />
                      <ais-index index-name="tenancy" />
                      <ais-index index-name="property" />
                      <ais-index index-name="condominium" />
                    </template>
                    <!-- eslint-disable vue/attribute-hyphenation -->
                    <ais-autocomplete v-if="!indexNameChanging">
                      <template #default="{ indices, refine }">
                        <vue-autosuggest
                          :key="refrecheAutosuggestKey"
                          v-model="emailSuggestion"
                          :input-props="{...inputProps, placeholder: $t('message.generic.search')}"
                          :suggestions="indicesToSuggestions(indices, contactHits)"
                          @input="onChange(refine, refineContacts, emailSuggestion)"
                          @selected="onSelect">
                          <template #default="{ suggestion }">
                            <span
                              :id="'tooltip-target_' + suggestion.item.objectID">
                              <template
                                v-if="suggestion.item.objectID.startsWith('tenancy')">
                                <div
                                  :class="checkSelectedElement(suggestion.item.objectID)"
                                  class="row m-0">
                                  <div
                                    class="p-0">
                                    <div
                                      v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.tenancy')}"
                                      class="color-box ml-2 mr-2 tenancy" />
                                  </div>

                                  <div class="col p-0">
                                    <p
                                      v-if="suggestion.item.objectID.startsWith('tenancy')"
                                      class="mb-0">
                                      <strong v-if="suggestion.item.tenants.length > 0">
                                        <span
                                          v-for="(tn, j) in suggestion.item.tenants"
                                          :key="j">
                                          {{ tn.name }} <span v-if="j < suggestion.item.tenants.length - 1">-</span>
                                        </span>
                                      </strong>
                                      <strong v-else>-</strong>
                                    </p>
                                    <p class="mb-0">
                                      {{ suggestion.item.startingAt | objectToDate }} -
                                      {{ suggestion.item.endingAt | objectToDate }}
                                    </p>
                                  </div>
                                  <span class="showSelected">
                                    {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                  </span>
                                </div>
                              </template>
                              <template
                                v-if="suggestion.item.objectID.startsWith('condominium')">
                                <div
                                  :class="checkSelectedElement(suggestion.item.objectID)"
                                  class="row m-0">
                                  <div
                                    class="p-0">
                                    <div
                                      v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.condo_owner')}"
                                      class="color-box ml-2 mr-2 condo_owner" />
                                  </div>

                                  <div class="col p-0">
                                    <p
                                      v-if="suggestion.item.objectID.startsWith('condominium')"
                                      class="mb-0">
                                      <strong>
                                        {{
                                          suggestion.item.condominiumOwners.length > 0 ? suggestion.item.condominiumOwners[0].name : '-'
                                        }}
                                      </strong>
                                    </p>
                                    <p class="mb-0">
                                      {{ suggestion.item.startingAt | objectToDate }} -
                                      {{ suggestion.item.endingAt | objectToDate }}
                                    </p>
                                  </div>
                                  <span class="showSelected">
                                    {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                  </span>
                                </div>
                              </template>
                              <template
                                v-else-if="suggestion.item.objectID.startsWith('obj')">
                                <div
                                  :class="checkSelectedElement(suggestion.item.objectID)"
                                  class="row m-0">
                                  <div
                                    class="p-0">
                                    <div
                                      v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.' + [suggestion.item.condominiumOwnership === true ? 'condominium' : 'object'])}"
                                      :class="[suggestion.item.condominiumOwnership === true ? 'condominium' : 'object']"
                                      class="color-box ml-2 mr-2" />
                                  </div>
                                  <div class="col p-0">
                                    <p class="mb-0">
                                      <b>{{ suggestion.item.name }}</b>
                                    </p>
                                    <p class="mb-0">
                                      <address-text
                                        :address="suggestion.item.address"
                                        :hide-if-empty="true"
                                        :show-short-country="true" />
                                    </p>
                                  </div>
                                  <span class="showSelected">
                                    {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                  </span>
                                </div>
                              </template>
                              <template
                                v-else-if="suggestion.item.objectID.startsWith('prop')">
                                <div
                                  :class="checkSelectedElement(suggestion.item.objectID)"
                                  class="row m-0">
                                  <div
                                    class="p-0">
                                    <div
                                      v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.property')}"
                                      class="color-box ml-2 mr-2 current" />
                                  </div>
                                  <div class="col p-0">
                                    <p class="mb-0">
                                      <b>{{ suggestion.item.name }}</b>
                                    </p>
                                    <p class="mb-0">
                                      <address-text
                                        :address="suggestion.item.address"
                                        :hide-if-empty="true"
                                        :show-short-country="true" />
                                    </p>
                                  </div>
                                  <span class="showSelected">
                                    {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                  </span>
                                </div>
                              </template>
                              <template
                                v-else-if="suggestion.item.objectID.startsWith('building')">
                                <div
                                  :class="checkSelectedElement(suggestion.item.objectID)"
                                  class="row m-0">
                                  <div
                                    class="p-0">
                                    <div
                                      v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.building')}"
                                      class="color-box ml-2 mr-2 not_activated" />
                                  </div>
                                  <div class="col p-0">
                                    <p class="mb-0">
                                      <b>{{ suggestion.item.name }}</b>
                                    </p>
                                    <p class="mb-0">
                                      <address-text
                                        :address="suggestion.item.address"
                                        :hide-if-empty="true"
                                        :show-short-country="true" />
                                    </p>
                                  </div>
                                  <span class="showSelected">
                                    {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                  </span>
                                </div>
                              </template>
                              <template v-else-if="suggestion.item.objectID.startsWith('contact')">
                                <div
                                  :class="checkSelectedElement(suggestion.item.objectID)"
                                  class="row m-0">
                                  <div
                                    class="p-0">
                                    <div
                                      v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.contact')}"
                                      class="color-box ml-2 mr-2 terminated" />
                                  </div>

                                  <div class="col p-0">
                                    <strong>{{ suggestion.item.lastName }} {{ suggestion.item.firstName }}</strong>
                                    <br>
                                    <address-text
                                      :address="suggestion.item.address"
                                      :hide-if-empty="true"
                                      :show-short-country="true" />
                                    <br>
                                    <span v-if="getListRole(suggestion.item).length > 0">
                                      {{ $t('message.generic.roles') }}: {{ getListRole(suggestion.item) }}
                                    </span>
                                  </div>
                                  <span class="showSelected">
                                    {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                  </span>
                                </div>
                              </template>
                              <b-tooltip
                                v-if="checkSelectedElement(suggestion.item.objectID) === 'selected'"
                                :container="'#list-connections'"
                                :target="'tooltip-target_' + suggestion.item.objectID"
                                placement="left"
                                triggers="hover">
                                {{ $t('message.generic.alreadySelected') }}
                              </b-tooltip>
                            </span>
                          </template>
                        </vue-autosuggest>
                      </template>
                    </ais-autocomplete>
                  </ais-instant-search>
                </template>
              </contact-search-provider>
            </div>
          </div>
        </div>
        <div class="col-sm-12 col-md-12 mb-2">
          <template v-if="isEditable">
            <label>{{ $t('message.calendar.reminder.employee') }}</label>
            <contact-search-select
              v-model="selectedEmployee"
              :filters="employeeSearchFilters"
              :clear-on-select="false"
              :close-on-select="true"
              :multiple="true"
              :placeholder="''"
              :preselect-first="false"
              :preserve-search="true"
              :taggable="false"
              :with-address="false"
              label="name"
              track-by="objectID" />
          </template>
          <template v-if="!isEditable">
            <label>{{ $t('message.calendar.reminder.employee') }}</label>
            <p data-cy="activity-modal-user-value">
              {{ selectedEmployee.length === 0 ? '-' : selectedEmployee.map(x => x.name).join(', ') }}
            </p>
          </template>
        </div>
        <div class="col-sm-12 col-md-12 mb-2">
          <label for="file">{{ $t('message.ticketOverview.ticket.attachment') }}</label>
          <coozzy-form-file-input
            v-if="isEditable"
            id="file"
            ref="coozzy-file-input"
            accept="text/plain, text/csv, application/pdf, image/*, video/*, .doc, .docx, .xls, .xlsx, .ppt, .pptx"
            @change="addFile" />
          <div class="row">
            <div
              v-for="media in mediasList"
              :key="media.id"
              class="media-block col-12">
              <div
                class="file-block p-1 mt-2 mb-0">
                <a
                  :href="media.url"
                  target="_blank">
                  {{ media.filename }}
                </a>
                <coozzy-button
                  v-if="isEditable"
                  class="delete-icon"
                  design="transparent"
                  @click="deleteMedia(media.id)">
                  <coozzy-delete-icon />
                </coozzy-button>
              </div>
            </div>
            <p
              v-if="!isEditable && mediasList.length === 0"
              data-cy="activity-modal-files-value"
              class="col-12">
              -
            </p>
          </div>
        </div>
      </div>
      <template #modal-footer>
        <div class="w-100 float-right">
          <coozzy-button
            v-if="!activityDetail && isEditable"
            :disabled="loading || uploadProcessing"
            class="float-right ml-3"
            design="green"
            @click="createActivity">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
          <coozzy-button
            v-else-if="isEditable"
            :disabled="loading || uploadProcessing"
            class="float-right ml-3"
            design="green"
            @click="updateActivity">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
          <coozzy-button
            :disabled="loading"
            design="default"
            @click="$bvModal.hide('modal-create-activity')">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-if="showDeleteConfirmationModal"
      id="deleteConfirmationModal"
      ref="deleteConfirmationModal"
      no-close-on-backdrop
      :title="$t('message.ticketOverview.activities.deleteActivity.title')"
      hide-footer>
      <p>{{ $t('message.ticketOverview.activities.deleteActivity.text') }}</p>
      <div class="col">
        <coozzy-button
          size="small"
          class="border mb-0"
          design="transparent"
          @click="hideDeleteConfirmationModal">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0"
          design="green"
          @click="confirmDelete">
          {{ $t('message.generic.delete') }}
        </coozzy-button>
      </div>
    </b-modal>
  </section>
</template>
<script>
import { required, requiredIf } from 'vuelidate/lib/validators'
import ContactApi from '../../misc/apis/ContactApi'
import AddressText from '@/framework/components/misc/AddressText'
import MediaApi from '../../misc/apis/MediaApi'
import { user } from '@/mixins/user'
import Vue from 'vue'
import ContactSearchSelect from '@/framework/components/search/ContactSearchSelect.vue'
import ContactSearchProvider from '@/framework/components/search/ContactSearchProvider.vue'
import CoozzySpinner from '../../framework/components/misc/CoozzySpinner'
import CoozzyFormInput from '../../framework/components/form/input/CoozzyFormInput'
import CoozzyFormTextarea from '../../framework/components/form/textarea/CoozzyFormTextarea'
import CoozzyFormSelect from '../../framework/components/form/select/CoozzyFormSelect'
import CoozzyButton from '../../framework/components/button/CoozzyButton'
// import CoozzyMultiselect from '../../framework/components/multiselect/CoozzyMultiselect'
import { routeChecks } from '@/mixins/routeChecks'
import CoozzyDeleteIcon from '../../framework/components/icons/CoozzyDeleteIcon'
import CoozzyFormFileInput from '@/framework/components/form/fileInput/CoozzyFormFileInput'
import { mapActions, mapGetters } from 'vuex'
import { algolia } from '@/mixins/algolia'
import { VueAutosuggest } from 'vue-autosuggest'
import { media } from '@/mixins/media'
import ObjectApi from '@/misc/apis/ObjectApi'
import RequestApi from '@/marketingBasic/api/RequestApi'
import CoozzyEditIcon from '@/framework/components/icons/CoozzyEditIcon.vue'
import CoozzyCheckMarkIcon from '@/framework/components/icons/CoozzyCheckMarkIcon'

export default {
  name: 'CreateActivityModal',
  components: {
    CoozzyEditIcon,
    CoozzyCheckMarkIcon,
    ContactSearchSelect,
    ContactSearchProvider,
    CoozzyFormFileInput,
    // CoozzyMultiselect,
    AddressText,
    VueAutosuggest,
    CoozzyDeleteIcon,
    CoozzyButton,
    CoozzyFormSelect,
    CoozzyFormTextarea,
    CoozzyFormInput,
    CoozzySpinner
  },
  mixins: [user, algolia, media, routeChecks],
  props: {
    activityDetail: {
      type: Object,
      default() {
        return null
      }
    },
    referencesIds: {
      type: Array,
      default() {
        return []
      }
    },
    contactPersons: {
      type: Array,
      default() {
        return []
      }
    },
    tenantList: {
      type: Array,
      default() {
        return []
      }
    },
    singleTenant: {
      type: Object,
      default() {
        return null
      }
    },
    contacts: {
      type: Array,
      default() {
        return []
      }
    },
    condominium: {
      type: Object,
      default() {
        return null
      }
    },
    ids: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      isEditable: this.activityDetail === null,
      selectedEmployee: [],
      loading: false,
      emailSuggestion: '',
      listEntities: [],
      debounceTimeout: null,
      refrecheAutosuggestKey: '',
      references: [],
      activity: {
        subject: '',
        text: '',
        timestampStart: null,
        timestampEnd: null,
        source: 'ACTIVITY_SOURCE_UNDEFINED',
        references: [],
        employeeContactId: '',
        mediaIds: [],
        hasStartTime: false,
        hasEndTime: false,
        billable: false,
        hourlyRate: null
      },
      time: {
        date: null,
        start: '',
        end: ''
      },
      skipNextEmployeeWatch: false,
      userOverrodeHourlyRate: false,
      timeList: [],
      employeeList: [],
      usedContacts: [],
      mediaUpload: null,
      mediasList: [],
      currentUser: null,
      typeOfConnection: null,
      availableConnectionTypes: [
        {
          key: 'property',
          value: 'property'
        },
        {
          key: 'building',
          value: 'building'
        },
        {
          key: 'object',
          value: 'object'
        },
        {
          key: 'condominium',
          value: 'object'
        },
        {
          key: 'tenancy',
          value: 'tenancy'
        },
        {
          key: 'condo_owner',
          value: 'condominium'
        },
        {
          key: 'contact',
          value: 'contact'
        }
      ],
      query: '',
      indexNameChanging: false,
      uploadProcessing: false,
      showDeleteConfirmationModal: false,
      modalHiddenHandler: null,
      modalShowHandler: null,
      referenceCache: {
        contacts: new Map(),
        object: new Map(),
        buildingByObject: new Map(),
        propertiesByBuilding: new Map(),
        tenancies: new Map(),
        condominiums: new Map(),
        properties: new Map(),
        buildings: new Map(),
        devices: new Map()
      }
    }
  },
  computed: {
    employeeSearchFilters() {
      return { active: true, isEmployee: true, ownerIds: [String(this.accountId)] }
    },
    duration() {
      if (this.activity.hasStartTime && this.activity.hasEndTime) {
        if (this.isEditable === false && this.activity.timestampStart && this.activity.timestampEnd) {
          const timestampStart = parseInt(this.activity.timestampStart)
          const timestampEnd = parseInt(this.activity.timestampEnd)
          const start = new Date(timestampStart).getTime()
          const end = new Date(timestampEnd).getTime()
          const diff = end - start
          const filterInstance = this.$options.filters
          return `${filterInstance.timestampToTimeOnly(diff)}`
        } else if (this.isEditable === true && this.time.start && this.time.end) {
          const dateStart = new Date(this.time.date)
          const dateEnd = new Date(this.time.date)
          const splitStartTime = this.time.start?.split(':')
          dateStart.setHours(splitStartTime[0])
          dateStart.setMinutes(splitStartTime[1])

          const splitEndTime = this.time.end?.split(':')
          dateEnd.setHours(splitEndTime[0])
          dateEnd.setMinutes(splitEndTime[1])

          const timeStampStart = dateStart.getTime() / 1000
          const timeStampEnd = dateEnd.getTime() / 1000
          const diff = timeStampEnd - timeStampStart
          const filterInstance = this.$options.filters
          return `${filterInstance.timestampToTimeOnly(diff)}`
        }
      }
      return '-'
    },
    timeOptions() {
      const options = [...this.timeList]
      for (const t of [this.time.start, this.time.end]) {
        if (t && !options.includes(t)) {
          options.push(t)
        }
      }
      return options.sort()
    },
    connectedEmployee() {
      const currentUser = this.$store.getters['user/getCurrentUser']
      return this.employeeList.find(object => object.email?.toLowerCase() === currentUser.email?.toLowerCase())
    },
    currentEmployee() {
      return this.employeeList.find(object => object.id === this.activity.employeeContactId)
    },
    canDeleteActivity() {
      return this.activityDetail && (this.isCompanyAdmin || this.activityDetail.creator?.userId === this.userId)
    },
    /** Contacts come from the search read model, so the Algolia client never queries that index. */
    contactsOnly() {
      return this.typeOfConnection?.value === 'contact'
    },
    includesContacts() {
      return this.typeOfConnection === null || this.contactsOnly
    },
    algoliaIndexName() {
      return this.typeOfConnection && !this.contactsOnly ? this.typeOfConnection.value : 'object'
    },
    connectionContactFilters() {
      return { isOwner: false, isEmployee: false, isOnlyInterestedParty: false }
    }
  },
  watch: {
    activity: {
      deep: true,
      handler: function () {
        this.$store.dispatch('user/setChangedModal', true).then().catch(e => console.log(e))
      }
    },
    typeOfConnection: function (item) {
      this.query = ''
      this.indexNameChanging = true
      if (item) {
        if (item.key === 'condominium') {
          this.query = 'condominiumOwnership:true'
        }
        if (item.key === 'object') {
          this.query = 'condominiumOwnership:false'
        }
      }
      setTimeout(() => {
        this.indexNameChanging = false
      }, 200)
    },
    isEditable: function (value) {
      if (value === true) {
        this.time.date = this.$options.filters.timestampToDateInput(this.activityDetail.timestampStart)
      }
    },
    'time.start': function (value) {
      this.activity.hasStartTime = value !== ''
    },
    'time.end': function (value) {
      this.activity.hasEndTime = value !== ''
    },
    selectedEmployee: {
      handler(newVal, oldVal) {
        if (this.skipNextEmployeeWatch) {
          this.skipNextEmployeeWatch = false
          return
        }
        if (!this.isEditable || !this.activity.billable) {
          return
        }
        const newLen = newVal?.length || 0
        const oldLen = oldVal?.length || 0
        // Default employee is often pushed async after open; do not wipe a rate the user already typed.
        if (this.userOverrodeHourlyRate && oldLen === 0 && newLen === 1) {
          return
        }
        if (newLen !== 1) {
          this.userOverrodeHourlyRate = false
          this.activity.hourlyRate = null
          return
        }
        if (this.userOverrodeHourlyRate && oldLen === 1) {
          const oldId = oldVal[0]?.objectID || oldVal[0]?.id
          const newId = newVal[0]?.objectID || newVal[0]?.id
          if (oldId === newId) {
            return
          }
          this.userOverrodeHourlyRate = false
        }
        this.prefillHourlyRateFromSelectedEmployee()
      },
      deep: true
    }
  },
  mounted: function () {
    this.getListTimes()
    this.loadEmployees()
    const userEmail = this.userEmail
    const accountId = this.accountId

    this.modalHiddenHandler = (bvEvent, modalId) => {
      if (modalId === 'modal-create-activity') {
        this.clearReferenceRequestCache()
        this.activity = {
          subject: '',
          text: '',
          timestampStart: null,
          timestampEnd: null,
          source: 'ACTIVITY_SOURCE_UNDEFINED',
          references: [],
          employeeContactId: '',
          mediaIds: [],
          hasStartTime: false,
          hasEndTime: false,
          billable: false,
          hourlyRate: null
        }
        this.skipNextEmployeeWatch = false
        this.userOverrodeHourlyRate = false
        this.mediasList = []
      }
    }

    this.modalShowHandler = (bvEvent, modalId) => {
      if (modalId === 'modal-create-activity') {
        this.clearReferenceRequestCache()
        if (this.activityDetail === null) {
          if (((typeof this.ids === 'string' || this.ids instanceof String) && this.ids !== '') || this.ids.length > 0) {
            let arr = []
            if (typeof this.ids === 'string' || this.ids instanceof String) {
              arr = [this.ids]
            } else {
              arr = this.ids
            }
            if (arr[0] !== null && arr[0] !== '' && arr[0] !== undefined) {
              if (arr[0].startsWith('device')) {
                this.getDevicesByIdsCached(arr)
                  .then(response => {
                    this.addDevicesToReferencesBulk(response.devices, false)
                  })
              } else if (arr[0].startsWith('condominium')) {
                this.getCondominiumsByIdsCached(arr)
                  .then(response => {
                    this.addCondominiumsToReferencesBulk(response.condominiums, false)
                  })
              } else if (arr[0].startsWith('tenancy')) {
                this.getTenanciesByIdsCached(arr)
                  .then(response => {
                    this.addTenanciesToReferencesBulk(response.tenancies, false)
                  })
              } else if (arr[0].startsWith('obj')) {
                ObjectApi.listObjectsByIds(arr)
                  .then(response => {
                    this.addObjectsToReferencesBulk(response.objects, false)
                  })
              } else if (arr[0].startsWith('building')) {
                this.getBuildingsByIdsCached(arr)
                  .then(response => {
                    this.addBuildingsToReferencesBulk(response.buildings, false)
                  })
              } else if (arr[0].startsWith('prop')) {
                this.getPropertiesByIdsCached(arr)
                  .then(response => {
                    this.addPropertiesToReferencesBulk(response.properties, false)
                  })
              } else if (arr[0].startsWith('req')) {
                RequestApi.listByIds(arr.filter(x => x.startsWith('request')))
                  .then(response => {
                    this.addRequestsToReferencesBulk(response.requests, false)
                  })
              } else if (arr[0].startsWith('contact')) {
                this.resolveContactsCached(arr)
                  .then(response => {
                    const contacts = response.persons.concat(response.companies).filter(x => arr.includes(x.id))
                    this.addContactsToReferencesBulk(contacts, false)
                  })
              }
            }
            this.listEntities = arr.filter(x => !x.startsWith('req'))
          }

          const today = new Date()
          let minutes = Math.ceil(today.getMinutes() / 15) * 15
          let hours = today.getHours()
          if (minutes === 60) {
            minutes = 0
            hours = (hours + 1) % 24
          }
          this.time.start = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
          this.time.date = this.$options.filters.timestampToDateInput(today.getTime() / 1000)
          this.activity.timestampStart = Math.round(today.getTime() / 1000)
          this.activity.employeeContactId = this.connectedEmployee ? this.connectedEmployee.id : ''
          ContactApi.resolveByMail(accountId, [userEmail])
            .then((response) => {
              if (response.persons.length > 0) {
                let person = response.persons[0]
                person.objectID = person.id
                this.selectedEmployee.push(person)
                this.currentUser = person.id
              } else if (response.companies.length > 0) {
                this.selectedEmployee.push(response.companies[0])
              }
            })
            .catch(e => {
              console.log(e)
              Vue.toasted.show(this.$t('message.savingErrors.activity'), { type: 'error' })
            })
        } else {
          this.activity = Object.assign({
            billable: false,
            hourlyRate: null
          }, this.activityDetail)
          if (this.activity.hourlyRate === undefined) {
            this.activity.hourlyRate = null
          }
          this.loadCurrentEmployee(this.activityDetail.employeeContactIds)
          this.time.date = this.formatDateForDateInput(this.activity.timestampStart)
          if (this.activity.hasStartTime) {
            this.time.start = this.formatTimeForCalendar(this.activity.timestampStart)
          }
          if (this.activity.hasEndTime && this.activity.timestampEnd) {
            this.time.end = this.formatTimeForCalendar(this.activity.timestampEnd)
          }
          if (this.activity?.references.length > 0) {
            this.mountedReference(this.activity.references)
          }
          if (this.activity.mediaIds?.length !== 0) {
            for (let index = 0; index < this.activity.mediaIds?.length; index++) {
              MediaApi.getMediaByID(this.activity.mediaIds[index])
                .then(response => {
                  this.mediasList.push(response.media)
                })
                .catch(e => {
                  console.log(e)
                  Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
                })
            }
          } else {
            this.mediasList = []
          }
        }
        this.updateTimestamp()

        this.$nextTick(function () {
          this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
        })
      }
    }

    this.$root.$on('bv::modal::hidden', this.modalHiddenHandler)
    this.$root.$on('bv::modal::show', this.modalShowHandler)
  },
  beforeDestroy() {
    if (this.modalHiddenHandler) {
      this.$root.$off('bv::modal::hidden', this.modalHiddenHandler)
    }
    if (this.modalShowHandler) {
      this.$root.$off('bv::modal::show', this.modalShowHandler)
    }
  },
  methods: {
    ...mapActions('employee', ['loadEmployees']),
    ...mapGetters('employee', ['getEmployees']),
    getEntityId(entity) {
      return entity?.id || entity?.objectID
    },
    getIdsCacheKey(ids = []) {
      return [...ids].filter(Boolean).sort().join(',')
    },
    getCachedRequest(cache, cacheKey, requestFn) {
      if (!cacheKey) {
        return requestFn()
      }
      if (!cache.has(cacheKey)) {
        const requestPromise = requestFn().catch(error => {
          cache.delete(cacheKey)
          throw error
        })
        cache.set(cacheKey, requestPromise)
      }
      return cache.get(cacheKey)
    },
    clearReferenceRequestCache() {
      this.referenceCache = {
        contacts: new Map(),
        object: new Map(),
        buildingByObject: new Map(),
        propertiesByBuilding: new Map(),
        tenancies: new Map(),
        condominiums: new Map(),
        properties: new Map(),
        buildings: new Map(),
        devices: new Map()
      }
    },
    resolveContactsCached(contactIds = []) {
      const ids = (contactIds || []).filter(Boolean)
      const key = this.getIdsCacheKey(ids)
      return this.getCachedRequest(this.referenceCache.contacts, key, () => ContactApi.contactResolve(ids))
    },
    getObjectCached(objectId) {
      return this.getCachedRequest(this.referenceCache.object, objectId, () => ObjectApi.getObject(objectId))
    },
    getBuildingByObjectIdCached(objectId) {
      return this.getCachedRequest(this.referenceCache.buildingByObject, objectId, () => ObjectApi.getBuildingByObjectId(objectId))
    },
    getPropertiesByBuildingIdCached(buildingId) {
      return this.getCachedRequest(this.referenceCache.propertiesByBuilding, buildingId, () => ObjectApi.getPropertiesByBuildingIds([buildingId]))
    },
    getTenanciesByIdsCached(ids = []) {
      const key = this.getIdsCacheKey(ids)
      return this.getCachedRequest(this.referenceCache.tenancies, key, () => ObjectApi.getTenanciesByIds(ids))
    },
    getCondominiumsByIdsCached(ids = []) {
      const key = this.getIdsCacheKey(ids)
      return this.getCachedRequest(this.referenceCache.condominiums, key, () => ObjectApi.getCondominiumByIds(ids))
    },
    getPropertiesByIdsCached(ids = []) {
      const key = this.getIdsCacheKey(ids)
      return this.getCachedRequest(this.referenceCache.properties, key, () => ObjectApi.getPropertyListByIds(ids))
    },
    getBuildingsByIdsCached(ids = []) {
      const key = this.getIdsCacheKey(ids)
      return this.getCachedRequest(this.referenceCache.buildings, key, () => ObjectApi.getBuildingsListByIds(ids))
    },
    getDevicesByIdsCached(ids = []) {
      const key = this.getIdsCacheKey(ids)
      return this.getCachedRequest(this.referenceCache.devices, key, () => ObjectApi.listDevicesByIds(ids))
    },
    createReferenceShell() {
      return {
        request: null,
        object: null,
        building: null,
        property: null,
        condominium: null,
        tenancy: null,
        contact: null,
        device: null,
        listContact: []
      }
    },
    getTenancyContactIds(tenancy) {
      if (tenancy?.tenant?.contactIds) {
        return tenancy.tenant.contactIds
      }
      if (tenancy?.tenants?.length > 0 && tenancy.tenants[0]?.contactIds) {
        return tenancy.tenants[0].contactIds
      }
      return []
    },
    indexBuildingsByObjectIds(buildings = []) {
      const buildingByObjectId = new Map()
      buildings.forEach(building => {
        const relatedObjectIds = []
        if (Array.isArray(building.objectIds)) {
          relatedObjectIds.push(...building.objectIds)
        }
        if (building.objectId) {
          relatedObjectIds.push(building.objectId)
        }
        if (Array.isArray(building.objects)) {
          building.objects.forEach(object => {
            const objectId = this.getEntityId(object)
            if (objectId) {
              relatedObjectIds.push(objectId)
            }
          })
        }
        relatedObjectIds.filter(Boolean).forEach(objectId => {
          if (!buildingByObjectId.has(objectId)) {
            buildingByObjectId.set(objectId, building)
          }
        })
      })
      return buildingByObjectId
    },
    indexPropertiesByBuildingIds(properties = []) {
      const propertyByBuildingId = new Map()
      properties.forEach(property => {
        const buildingIds = Array.isArray(property.buildingIds) ? property.buildingIds : []
        buildingIds.forEach(buildingId => {
          if (buildingId && !propertyByBuildingId.has(buildingId)) {
            propertyByBuildingId.set(buildingId, property)
          }
        })
      })
      return propertyByBuildingId
    },
    addTenanciesToReferencesBulk(tenancies = [], fromInput = true) {
      if (!Array.isArray(tenancies) || tenancies.length === 0) {
        return
      }

      const startIndex = this.references.length
      tenancies.forEach(tenancy => {
        const entry = this.createReferenceShell()
        entry.tenancy = tenancy
        this.references.push(entry)
      })

      const objectIds = [...new Set(tenancies.map(tenancy => tenancy.obj?.id || tenancy.objectId).filter(Boolean))]
      const allContactIds = [...new Set([].concat(...tenancies.map(tenancy => this.getTenancyContactIds(tenancy))).filter(Boolean))]

      const objectById = new Map()
      const buildingByObjectId = new Map()
      let propertyByBuildingId = new Map()
      const contactById = new Map()

      const requests = []
      if (objectIds.length > 0) {
        requests.push(
          ObjectApi.listObjectsByIds(objectIds).then(response => {
            response.objects.forEach(object => {
              objectById.set(this.getEntityId(object), object)
            })
          })
        )
        requests.push(
          ObjectApi.getBuildingByObjectIds(objectIds).then(response => {
            const indexedBuildings = this.indexBuildingsByObjectIds(response.buildings || [])
            indexedBuildings.forEach((building, objectId) => {
              buildingByObjectId.set(objectId, building)
            })
            const buildingIds = [...new Set((response.buildings || []).map(building => this.getEntityId(building)).filter(Boolean))]
            if (buildingIds.length > 0) {
              return ObjectApi.getPropertiesByBuildingIds(buildingIds).then(propertiesResponse => {
                propertyByBuildingId = this.indexPropertiesByBuildingIds(propertiesResponse.properties || [])
              })
            }
            return null
          })
        )
      }
      if (allContactIds.length > 0) {
        requests.push(
          this.resolveContactsCached(allContactIds).then(response => {
            response.persons.concat(response.companies).forEach(contact => {
              contactById.set(contact.id, contact)
            })
          })
        )
      }

      Promise.all(requests)
        .then(() => {
          tenancies.forEach((tenancy, offset) => {
            const referenceIndex = startIndex + offset
            const objectId = tenancy.obj?.id || tenancy.objectId
            const object = objectById.get(objectId)
            const building = buildingByObjectId.get(objectId)
            const property = building ? propertyByBuildingId.get(this.getEntityId(building)) : null
            const contactIds = this.getTenancyContactIds(tenancy)

            this.references[referenceIndex].object = object || this.references[referenceIndex].object
            this.references[referenceIndex].building = building || this.references[referenceIndex].building
            this.references[referenceIndex].property = property || this.references[referenceIndex].property
            this.references[referenceIndex].listContact = contactIds.map(id => contactById.get(id)).filter(Boolean)
          })

          if (!fromInput) {
            this.$nextTick(() => {
              this.emailSuggestion = ''
              this.refrecheAutosuggestKey = Math.floor(Math.random() * 100)
            })
          }
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
        })
    },
    primeIndividualCacheEntries(cache, items = []) {
      items.forEach(item => {
        const id = this.getEntityId(item)
        if (!id) {
          return
        }
        if (cache === this.referenceCache.buildings) {
          cache.set(id, Promise.resolve({ buildings: [item] }))
        } else if (cache === this.referenceCache.properties) {
          cache.set(id, Promise.resolve({ properties: [item] }))
        } else if (cache === this.referenceCache.object) {
          cache.set(id, Promise.resolve({ object: item }))
        }
      })
    },
    primeObjectBuildingPropertyByObjectIds(objectIds = []) {
      const uniqueObjectIds = [...new Set(objectIds.filter(Boolean))]
      if (uniqueObjectIds.length === 0) {
        return Promise.resolve()
      }
      return Promise.all([
        ObjectApi.listObjectsByIds(uniqueObjectIds).then(response => {
          this.primeIndividualCacheEntries(this.referenceCache.object, response.objects || [])
        }),
        ObjectApi.getBuildingByObjectIds(uniqueObjectIds).then(response => {
          const buildings = response.buildings || []
          const buildingByObjectId = this.indexBuildingsByObjectIds(buildings)
          buildingByObjectId.forEach((building, objectId) => {
            this.referenceCache.buildingByObject.set(objectId, Promise.resolve({ building }))
          })
          const buildingIds = [...new Set(buildings.map(building => this.getEntityId(building)).filter(Boolean))]
          this.primeIndividualCacheEntries(this.referenceCache.buildings, buildings)
          if (buildingIds.length > 0) {
            return ObjectApi.getPropertiesByBuildingIds(buildingIds).then(responseProp => {
              const properties = responseProp.properties || []
              const propertyByBuildingId = this.indexPropertiesByBuildingIds(properties)
              propertyByBuildingId.forEach((property, buildingId) => {
                this.referenceCache.propertiesByBuilding.set(buildingId, Promise.resolve({ properties: [property] }))
              })
              this.primeIndividualCacheEntries(this.referenceCache.properties, properties)
            })
          }
          return null
        })
      ]).catch(e => {
        console.log(e)
      })
    },
    primeContactsForGroups(contactGroups = []) {
      const groups = contactGroups.map(group => (group || []).filter(Boolean)).filter(group => group.length > 0)
      const allContactIds = [...new Set([].concat(...groups))]
      if (allContactIds.length === 0) {
        return Promise.resolve()
      }
      return this.resolveContactsCached(allContactIds).then(response => {
        const contactsById = new Map()
        response.persons.concat(response.companies).forEach(contact => {
          contactsById.set(contact.id, contact)
        })
        groups.forEach(group => {
          const key = this.getIdsCacheKey(group)
          const contacts = group.map(id => contactsById.get(id)).filter(Boolean)
          const persons = contacts.filter(contact => contact.objectID?.startsWith('contact_person'))
          const companies = contacts.filter(contact => !contact.objectID?.startsWith('contact_person'))
          this.referenceCache.contacts.set(key, Promise.resolve({ persons, companies }))
        })
      }).catch(e => {
        console.log(e)
      })
    },
    addObjectsToReferencesBulk(objects = [], fromInput = true) {
      const objectIds = objects.map(object => this.getEntityId(object))
      this.primeObjectBuildingPropertyByObjectIds(objectIds).finally(() => {
        objects.forEach(object => {
          this.addToReferences({ item: object }, fromInput)
        })
      })
    },
    addBuildingsToReferencesBulk(buildings = [], fromInput = true) {
      const buildingIds = buildings.map(building => this.getEntityId(building)).filter(Boolean)
      if (buildingIds.length === 0) {
        return
      }
      ObjectApi.getPropertiesByBuildingIds(buildingIds).then(response => {
        const properties = response.properties || []
        const propertyByBuildingId = this.indexPropertiesByBuildingIds(properties)
        propertyByBuildingId.forEach((property, buildingId) => {
          this.referenceCache.propertiesByBuilding.set(buildingId, Promise.resolve({ properties: [property] }))
        })
        this.primeIndividualCacheEntries(this.referenceCache.properties, properties)
      }).catch(e => {
        console.log(e)
      }).finally(() => {
        buildings.forEach(building => {
          this.addToReferences({ item: building }, fromInput)
        })
      })
    },
    addPropertiesToReferencesBulk(properties = [], fromInput = true) {
      this.primeIndividualCacheEntries(this.referenceCache.properties, properties)
      properties.forEach(property => {
        this.addToReferences({ item: property }, fromInput)
      })
    },
    addCondominiumsToReferencesBulk(condominiums = [], fromInput = true) {
      const objectIds = condominiums.map(condominium => condominium.objectId)
      const ownerContactGroups = condominiums.map(condominium => condominium?.owner?.contactIds || [])
      Promise.all([
        this.primeObjectBuildingPropertyByObjectIds(objectIds),
        this.primeContactsForGroups(ownerContactGroups)
      ]).finally(() => {
        condominiums.forEach(condominium => {
          this.addToReferences({ item: condominium }, fromInput)
        })
      })
    },
    addDevicesToReferencesBulk(devices = [], fromInput = true) {
      const objectIds = devices.map(device => device.referenceId).filter(id => id?.startsWith('obj'))
      const buildingIds = devices.map(device => device.referenceId).filter(id => id?.startsWith('building'))
      const propertyIds = devices.map(device => device.referenceId).filter(id => id?.startsWith('prop'))
      Promise.all([
        this.primeObjectBuildingPropertyByObjectIds(objectIds),
        this.getBuildingsByIdsCached(buildingIds).then(response => {
          this.primeIndividualCacheEntries(this.referenceCache.buildings, response.buildings || [])
          const resolvedBuildingIds = (response.buildings || []).map(building => this.getEntityId(building)).filter(Boolean)
          if (resolvedBuildingIds.length > 0) {
            return ObjectApi.getPropertiesByBuildingIds(resolvedBuildingIds).then(responseProp => {
              const properties = responseProp.properties || []
              const propertyByBuildingId = this.indexPropertiesByBuildingIds(properties)
              propertyByBuildingId.forEach((property, buildingId) => {
                this.referenceCache.propertiesByBuilding.set(buildingId, Promise.resolve({ properties: [property] }))
              })
              this.primeIndividualCacheEntries(this.referenceCache.properties, properties)
            })
          }
          return null
        }).catch(e => {
          console.log(e)
        }),
        this.getPropertiesByIdsCached(propertyIds).then(response => {
          this.primeIndividualCacheEntries(this.referenceCache.properties, response.properties || [])
        }).catch(e => {
          console.log(e)
        })
      ]).finally(() => {
        devices.forEach(device => {
          this.addToReferences({ item: device }, fromInput)
        })
      })
    },
    addRequestsToReferencesBulk(requests = [], fromInput = true) {
      const requesterContactGroups = requests.map(request => request.requesters.map(requester => requester.contactId).filter(Boolean))
      this.primeContactsForGroups(requesterContactGroups).finally(() => {
        requests.forEach(request => {
          this.addToReferences({ item: request }, fromInput)
        })
      })
    },
    addContactsToReferencesBulk(contacts = [], fromInput = true) {
      this.primeContactsForGroups(contacts.map(contact => [contact.id || contact.objectID]))
      contacts.forEach(contact => {
        this.addToReferences({ item: contact }, fromInput)
      })
    },
    populateObjectBuildingProperty(referenceIndex, objectId) {
      if (!objectId) {
        return
      }
      this.getObjectCached(objectId)
        .then(response => {
          this.references[referenceIndex].object = response.object
          return this.getBuildingByObjectIdCached(this.references[referenceIndex].object.id)
        })
        .then(response => {
          this.references[referenceIndex].building = response.building
          return this.getPropertiesByBuildingIdCached(this.references[referenceIndex].building.id)
        })
        .then(response => {
          this.references[referenceIndex].property = response.properties[0]
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
        })
    },
    deleteActivityClicked() {
      this.showDeleteConfirmationModal = true
      this.$nextTick(() => {
        this.$refs.deleteConfirmationModal.show()
      })
    },
    hideDeleteConfirmationModal() {
      this.$refs.deleteConfirmationModal.hide()
      this.showDeleteConfirmationModal = false
    },
    confirmDelete() {
      if (this.activityDetail) {
        ContactApi.deleteActivity(this.activityDetail.id)
          .then(() => {
            this.hideDeleteConfirmationModal()
            this.$emit('activity-deleted', this.activityDetail)
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.savingApplicationForm'), { type: 'error' })
          })
      }
    },
    getListRole(contact) {
      if (contact !== null && contact.types !== undefined) {
        if (contact.types && contact.types.length === 1 && contact.types[0] === 'NONE') {
          return '-'
        } else {
          const array = []
          contact.types.forEach(role => {
            if (role !== 'NONE') {
              array.push(this.$t('message.contact.types.' + role.toUpperCase()))
            }
          })
          return array.join(', ')
        }
      }
      return '-'
    },
    indicesToSuggestions(indices, contactHits) {
      const algoliaGroups = this.contactsOnly ? [] : indices.map(({ hits }) => ({ data: hits }))
      return this.includesContacts ? algoliaGroups.concat([{ data: contactHits }]) : algoliaGroups
    },
    redirectToContactDetail(contactId) {
      if (contactId !== null) {
        if (this.isOwnerModule) {
          return {
            name: 'OwnerContactDetailsView',
            params: { id: contactId }
          }
        } else if (this.isAssetModule) {
          return {
            name: 'AssetContactDetailsView',
            params: { id: contactId }
          }
        } else if (this.isAccountingModule) {
          return {
            name: 'AccountingContactDetailsView',
            params: { id: contactId }
          }
        } else if (this.isMarketingModule) {
          return {
            name: 'MarketingContactDetailsView',
            params: { id: contactId }
          }
        } else {
          return {
            name: 'AdminContactDetailsView',
            params: { id: contactId }
          }
        }
      }
    },
    redirectToBuildingDetail(buildingId) {
      if (this.isOwnerModule) {
        return {
          name: 'OwnerBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      } else if (this.isAssetModule) {
        return {
          name: 'AssetBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      } else if (this.isAccountingModule) {
        return {
          name: 'AccountingBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      } else if (this.isAdminModule) {
        return {
          name: 'AdminBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      }
    },
    redirectToPropertyDetail(propertyId) {
      if (this.isOwnerModule) {
        return {
          name: 'OwnerPropertyDetailsView',
          params: { id: propertyId }
        }
      } else if (this.isAdminModule) {
        return {
          name: 'AdminPropertyDetailsView',
          params: { id: propertyId }
        }
      } else if (this.isAssetModule) {
        return {
          name: 'AssetPropertyDetailsView',
          params: { id: propertyId }
        }
      } else if (this.isAccountingModule) {
        return {
          name: 'AccountingPropertyDetailsView',
          params: { id: propertyId }
        }
      }
    },
    redirectToRequestDetail(objectId, requestId) {
      return {
        name: 'MarketingObjectDetailsView',
        params: { id: objectId },
        query: {
          view: 0,
          elementSelected: requestId
        }
      }
    },
    redirectToDetail(buildingId, objectId, view) {
      if (this.isOwnerModule) {
        return {
          name: 'OwnerBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      } else if (this.isAdminModule) {
        return {
          name: 'AdminBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      } else if (this.isAssetModule) {
        return {
          name: 'AssetBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      } else if (this.isAccountingModule) {
        return {
          name: 'AccountingBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      }
    },
    onSelect(selected) {
      if (selected) {
        let alreadyExist = false
        const id = selected.item.objectID
        this.listEntities.push(id)
        if (selected.item.objectID.startsWith('condominium')) {
          if (this.references.find(x => x.condominium && (x.condominium.id === id || x.condominium.objectID === id))) {
            this.references = this.references.filter(x => !x.condominium || (x.condominium && ((x.condominium.objectID && x.condominium.objectID !== id) || (x.condominium.id && x.condominium.id !== id))))
            alreadyExist = true
          }
        }
        if (selected.item.objectID.startsWith('tenancy')) {
          if (this.references.find(x => x.tenancy && (x.tenancy.id === id || x.tenancy.objectID === id))) {
            this.references = this.references.filter(x => !x.tenancy || (x.tenancy && ((x.tenancy.objectID && x.tenancy.objectID !== id) || (x.tenancy.id && x.tenancy.id !== id))))
            alreadyExist = true
          }
        }
        if (selected.item.objectID.startsWith('obj')) {
          if (this.references.find(x => x.object && (x.object.id === id || x.object.objectID === id) && (!x.tenancy && !x.condominium))) {
            this.references = this.references.filter(x => !x.object || (x.object && ((x.object.objectID && x.object.objectID !== id) || (x.object.id && x.object.id !== id) || (x.tenancy || x.condominium))))
            alreadyExist = true
          }
        }
        if (selected.item.objectID.startsWith('building')) {
          if (this.references.find(x => x.building && (x.building.id === id || x.building.objectID === id) && !x.object)) {
            this.references = this.references.filter(x => !x.building || (x.building && ((x.building.objectID && x.building.objectID !== id) || (x.building.id && x.building.id !== id) || x.object)))
            alreadyExist = true
          }
        }
        if (selected.item.objectID.startsWith('prop')) {
          if (this.references.find(x => x.property && (x.property.id === id || x.property.objectID === id) && !x.building)) {
            this.references = this.references.filter(x => !x.property || (x.property && ((x.property.objectID && x.property.objectID !== id) || (x.property.id && x.property.id !== id) || x.building)))
            alreadyExist = true
          }
        }
        if (selected.item.objectID.startsWith('contact')) {
          if (this.references.find(x => x.contact && (x.contact.id === id || x.contact.objectID === id))) {
            this.references = this.references.filter(x => !x.contact || (x.contact && ((x.contact.objectID && x.contact.objectID !== id) || (x.contact.id && x.contact.id !== id))))
            alreadyExist = true
          }
        }
        if (alreadyExist === false) {
          this.addToReferences(selected)
        }
        this.$nextTick(function () {
          this.emailSuggestion = ''
          this.refrecheAutosuggestKey = Math.floor(Math.random() * 100)
        })
      }
    },
    addToReferences(selected, fromInput = true) {
      this.emailSuggestion = null
      const lengthOfConnections = this.references.length
      this.references.push({
        request: null,
        object: null,
        building: null,
        property: null,
        condominium: null,
        tenancy: null,
        contact: null,
        device: null,
        listContact: []
      })
      if (selected.item.objectID?.startsWith('condominium') || selected.item.id?.startsWith('condominium') || selected.item.objectID?.startsWith('tenancy') || selected.item.id?.startsWith('tenancy')) {
        if (selected.item.objectID?.startsWith('condominium') || selected.item.id?.startsWith('condominium')) {
          this.references[lengthOfConnections].condominium = selected.item
          if (selected.item.id || selected.item.objectID) {
            this.resolveContactsCached(selected.item.owner.contactIds)
              .then(responseCon => {
                this.references[lengthOfConnections].listContact = responseCon.persons.concat(responseCon.companies).filter(c => selected.item.owner.contactIds.includes(c.id))
              })
          }
        } else if (selected.item.objectID?.startsWith('tenancy') || selected.item.id?.startsWith('tenancy')) {
          this.references[lengthOfConnections].tenancy = selected.item
          if (selected.item.id || selected.item.objectID) {
            const tenant = selected.item.tenant ? selected.item.tenant.contactIds : selected.item.tenants[0].contactIds
            this.resolveContactsCached(tenant)
              .then(responseCon => {
                this.references[lengthOfConnections].listContact = responseCon.persons.concat(responseCon.companies).filter(c => tenant.includes(c.id))
              })
          }
        }
        this.populateObjectBuildingProperty(lengthOfConnections, selected.item.obj?.id || selected.item.objectId)
      }
      if (selected.item.objectID?.startsWith('obj') || selected.item.id?.startsWith('obj')) {
        this.references[lengthOfConnections].object = selected.item
        // add address of object to description
        if (!fromInput && selected.item && this.fromBulk && this.ticketType === 'TICKET_TYPE_ORDER') {
          this.ticket.description += '\n' + selected.item.name
          this.ticket.description += '\n' + this.$t('message.onBoarding.buildings.objects.category.' + selected.item.category.toLowerCase()) + (selected.item.characteristics?.floor !== null ? ' - ' + this.formatFloor(selected.item.characteristics?.floor) : '')
          this.ticket.description += '\n' + this.convertAddress(selected.item.address) + '\n'
        }
        this.getBuildingByObjectIdCached(selected.item.objectID || selected.item.id).then(response => {
          this.references[lengthOfConnections].building = response.building
          this.getPropertiesByBuildingIdCached(this.references[lengthOfConnections].building.id)
            .then(response => {
              this.references[lengthOfConnections].property = response.properties[0]
            })
        }).catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
        })
      }
      if (selected.item.objectID?.startsWith('building') || selected.item.id?.startsWith('building')) {
        this.references[lengthOfConnections].building = selected.item
        // add address of building to description
        if (!fromInput && selected.item && this.fromBulk && this.ticketType === 'TICKET_TYPE_ORDER') {
          this.ticket.description += '\n' + selected.item.name
          this.ticket.description += '\n' + this.convertAddress(selected.item.address) + '\n'
        }
        this.getPropertiesByBuildingIdCached(selected.item.objectID || selected.item.id)
          .then(response => {
            this.references[lengthOfConnections].property = response.properties[0]
          })
      }
      if (selected.item.objectID?.startsWith('prop') || selected.item.id?.startsWith('prop')) {
        this.references[lengthOfConnections].property = selected.item
        // add the addresses of all the buildings of a property to the description
        if (!fromInput && selected.item?.buildingIds.length > 0 && this.fromBulk && this.ticketType === 'TICKET_TYPE_ORDER') {
          this.getBuildingsByIdsCached(selected.item.buildingIds).then(response => {
            this.ticket.description += '\n' + selected.item.name
            response.buildings.forEach(element => {
              this.ticket.description += '\n' + this.convertAddress(element.address)
            })
            this.$nextTick(() => {
              this.ticket.description += '\n'
            })
          })
        }
      }
      if (selected.item.objectID?.startsWith('contact') || selected.item.id?.startsWith('contact')) {
        this.references[lengthOfConnections].contact = selected.item
      }
      if (selected.item.objectID?.startsWith('device') || selected.item.id?.startsWith('device')) {
        this.references[lengthOfConnections].device = selected.item

        if (selected.item.referenceId?.startsWith('obj')) {
          this.populateObjectBuildingProperty(lengthOfConnections, selected.item.referenceId)
        }
        if (selected.item.referenceId?.startsWith('building')) {
          this.getBuildingsByIdsCached([selected.item.referenceId])
            .then(response => {
              this.references[lengthOfConnections].building = response.buildings[0]
              return this.getPropertiesByBuildingIdCached(response.buildings[0].id)
            })
            .then(response => {
              this.references[lengthOfConnections].property = response.properties[0]
            })
        }
        if (selected.item.referenceId?.startsWith('prop')) {
          this.getPropertiesByIdsCached([selected.item.referenceId])
            .then(response => {
              this.references[lengthOfConnections].property = response.properties[0]
            })
        }
      }
      if (selected.item.objectID?.startsWith('req') || selected.item.id?.startsWith('req')) {
        this.references[lengthOfConnections].request = selected.item
        if (selected.item.id || selected.item.objectID) {
          const cont = selected.item.requesters.map(r => r.contactId)
          this.resolveContactsCached(cont)
            .then(response => {
              this.references[lengthOfConnections].listContact = response.persons.concat(response.companies).filter(c => cont.includes(c.id))
            })
        }
      }
    },
    onChange(refine, refineContacts, email) {
      if (email && email.length > 0) {
        refineContacts(email)
        if (email !== '') clearTimeout(this.debounceTimeout)
        this.debounceTimeout = setTimeout(() => {
          this.debounceTimeout = null
          refine(email)
        }, 300)
      }
    },
    formatDateForDateInput(date) {
      return this.$options.filters.timestampToDate(date)
    },
    async mountedReference(references) {
      const condominiumIds = references.map(reference => reference.id).filter(id => id?.startsWith('condominium'))
      const tenancyIds = references.map(reference => reference.id).filter(id => id?.startsWith('tenancy'))
      const condominiumsById = new Map()
      const tenanciesById = new Map()

      try {
        if (condominiumIds.length > 0) {
          const response = await this.getCondominiumsByIdsCached(condominiumIds)
          response.condominiums.forEach(condominium => {
            condominiumsById.set(this.getEntityId(condominium), condominium)
          })
        }
        if (tenancyIds.length > 0) {
          const response = await this.getTenanciesByIdsCached(tenancyIds)
          response.tenancies.forEach(tenancy => {
            tenanciesById.set(this.getEntityId(tenancy), tenancy)
          })
        }
      } catch (e) {
        console.log(e)
      }

      for (let index = 0; index < references.length; index++) {
        this.references.push({
          object: null,
          building: null,
          property: null,
          condominium: null,
          tenancy: null,
          contact: null,
          device: null,
          request: null,
          listContact: []
        })
        const element = references[index].id
        this.listEntities.push(element)

        if (element.startsWith('condominium') || element.startsWith('tenancy')) {
          if (element.startsWith('condominium')) {
            const condominium = condominiumsById.get(element)
            if (condominium) {
              this.references[index].condominium = condominium
              this.resolveContactsCached(condominium.owner.contactIds)
                .then(responseCon => {
                  this.references[index].listContact = responseCon.persons.concat(responseCon.companies)
                })
              this.populateObjectBuildingProperty(index, condominium.objectId)
            }
          } else if (element.startsWith('tenancy')) {
            const tenancy = tenanciesById.get(element)
            if (tenancy) {
              this.references[index].tenancy = tenancy
              this.resolveContactsCached(tenancy.tenant.contactIds)
                .then(responseCon => {
                  this.references[index].listContact = responseCon.persons.concat(responseCon.companies).filter(c => tenancy.tenant.contactIds.includes(c.id))
                })
              this.populateObjectBuildingProperty(index, tenancy.objectId)
            }
          }
        }
        if (element.startsWith('device')) {
          this.getDevicesByIdsCached([element])
            .then(response => {
              this.references[index].device = response.devices[0]
              if (response.devices[0].referenceId?.startsWith('obj')) {
                this.populateObjectBuildingProperty(index, response.devices[0].referenceId)
              }
              if (response.devices[0].referenceId?.startsWith('building')) {
                this.getBuildingsByIdsCached([response.devices[0].referenceId])
                  .then(response => {
                    this.references[index].building = response.buildings[0]
                    return this.getPropertiesByBuildingIdCached(response.buildings[0].id)
                  })
                  .then(response => {
                    this.references[index].property = response.properties[0]
                  })
              }
              if (response.devices[0].referenceId?.startsWith('prop')) {
                this.getPropertiesByIdsCached([response.devices[0].referenceId])
                  .then(response => {
                    this.references[index].property = response.properties[0]
                  })
              }
            })
        }
        if (element.startsWith('request')) {
          RequestApi.listByIds([element])
            .then(response => {
              this.references[index].request = response.requests[0]
              const cont = response.requests[0].requesters[0].contactId
              this.resolveContactsCached([cont])
                .then(response => {
                  this.references[index].listContact = response.persons.concat(response.companies).filter(c => cont.includes(c.id))
                })
            })
        }
        if (element.startsWith('obj')) {
          this.populateObjectBuildingProperty(index, element)
        }
        if (element.startsWith('building')) {
          this.getBuildingsByIdsCached([element]).then(response => {
            this.references[index].building = response.buildings[0]
            this.getPropertiesByBuildingIdCached(this.references[index].building.id)
              .then(response => {
                this.references[index].property = response.properties[0]
              })
          })
        }
        if (element.startsWith('prop')) {
          this.getPropertiesByIdsCached([element])
            .then(response => {
              this.references[index].property = response.properties[0]
            })
        }
        if (element.startsWith('contact')) {
          this.resolveContactsCached([element])
            .then(response => {
              this.references[index].contact = (response.persons.concat(response.companies)).find(x => x.id === element)
            })
        }
      }
    },
    removeReference(index) {
      this.listEntities = this.listEntities.filter((element, i) => i !== index)
      this.$delete(this.references, index)
    },
    checkSelectedElement(id) {
      if (id.startsWith('condominium')) {
        if (this.references.find(x => x.condominium && (x.condominium.id === id || x.condominium.objectID === id))) {
          return 'selected'
        }
      }
      if (id.startsWith('tenancy')) {
        if (this.references.find(x => x.tenancy && (x.tenancy.id === id || x.tenancy.objectID === id))) {
          return 'selected'
        }
      }
      if (id.startsWith('obj')) {
        if (this.references.find(x => x.object && (x.object.id === id || x.object.objectID === id) && (!x.tenancy && !x.condominium))) {
          return 'selected'
        }
      }
      if (id.startsWith('building')) {
        if (this.references.find(x => x.building && (x.building.id === id || x.building.objectID === id) && !x.object)) {
          return 'selected'
        }
      }
      if (id.startsWith('prop')) {
        if (this.references.find(x => x.property && (x.property.id === id || x.property.objectID === id) && !x.building)) {
          return 'selected'
        }
      }
      if (id.startsWith('contact')) {
        if (this.references.find(x => x.contact && (x.contact.id === id || x.contact.objectID === id))) {
          return 'selected'
        }
      }
      return ''
    },
    formatTimeForCalendar(time) {
      const date = new Date(time * 1000)
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    },
    getListTimes() {
      let hours, minutes
      const list = []
      for (let i = 0; i <= 1425; i += 15) {
        hours = Math.floor(i / 60)
        minutes = i % 60
        if (minutes < 10) {
          minutes = '0' + minutes
        }
        if (hours < 10) {
          hours = '0' + hours
        }
        if (hours === 24) {
          hours = '00'
        }
        list.push(hours + ':' + minutes)
      }
      this.timeList = list
    },
    see(refine, value) {
      refine(value)
    },
    updateTimestamp() {
      // for calcule start time on edit mode
      if (this.time.date !== '' && this.isEditable) {
        const start = new Date(this.time.date)
        if (this.time.start) {
          const splitTime = this.time.start?.split(':')
          start.setHours(splitTime[0])
          start.setMinutes(splitTime[1])
        }
        if (!isNaN(start)) {
          this.activity.timestampStart = start.getTime() / 1000
        }
      }
      // for calcule end time on edit mode
      if (this.time.date !== '' && this.isEditable) {
        const end = new Date(this.time.date)
        if (this.time.end) {
          const splitTime = this.time.end?.split(':')
          end.setHours(splitTime[0])
          end.setMinutes(splitTime[1])
        }
        if (!isNaN(end)) {
          this.activity.timestampEnd = end.getTime() / 1000
        }
      }
      if (this.time.end === '') {
        this.activity.timestampEnd = null
        this.activity.hasEndTime = false
      }
      if (this.time.start === '') {
        this.activity.hasStartTime = false
      }
      if (this.time.date === '') {
        this.activity.timestampStart = null
        this.activity.timestampEnd = null
      }
    },
    async addFile(event) {
      const files = await this.getFilesFromEvent(event)
      if (files && files.length > 0) {
        this.mediaUpload = files[0]
        await this.sendFile()
      }
    },
    async sendFile() {
      this.uploadProcessing = true
      const selectedFile = this.mediaUpload ? [this.mediaUpload] : []
      const response = await this.uploadMedia(selectedFile)
      if (response?.length > 0) {
        const media = response[0]
        this.mediasList.push(media)
        this.activity.mediaIds.push(media.id)
        this.mediaUpload = null
        if (this.$refs['coozzy-file-input']) {
          this.$refs['coozzy-file-input'].resetPlaceholder()
        }
      }
      this.uploadProcessing = false
    },
    createActivity() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.loading = true
        this.activity.employeeContactIds = this.selectedEmployee.map(x => x.objectID)
        const connectionReferences = []
        this.references.forEach(element => {
          if (element.contact) {
            const id = element.contact.id ? element.contact.id : element.contact.objectID
            connectionReferences.push({
              id: id,
              relatedIds: []
            })
          } else if (element.tenancy) {
            const id = element.tenancy.id ? element.tenancy.id : element.tenancy.objectID
            const idProperty = element.property.id ? element.property.id : element.property.objectID
            const idBuilding = element.building.id ? element.building.id : element.building.objectID
            const idObject = element.object.id ? element.object.id : element.object.objectID
            connectionReferences.push({
              id: id,
              relatedIds: [idProperty, idBuilding, idObject]
            })
          } else if (element.request) {
            const id = element.request.id ? element.request.id : element.request.objectID
            connectionReferences.push({
              id: id,
              relatedIds: []
            })
          } else if (element.condominium) {
            const id = element.condominium.id ? element.condominium.id : element.condominium.objectID
            const idProperty = element.property.id ? element.property.id : element.property.objectID
            const idBuilding = element.building.id ? element.building.id : element.building.objectID
            const idObject = element.object.id ? element.object.id : element.object.objectID
            connectionReferences.push({
              id: id,
              relatedIds: [idProperty, idBuilding, idObject]
            })
          } else if (element.device) {
            const id = element.device.id ? element.device.id : element.device.objectID
            let idProperty
            let idBuilding = null
            let idObject = null
            let ids
            if (element.object) {
              idProperty = element.property.id ? element.property.id : element.property.objectID
              idBuilding = element.building?.id ? element.building.id : element.building.objectID
              idObject = element.object?.id ? element.object.id : element.object.objectID

              ids = [idProperty, idBuilding, idObject]
            } else if (element.building) {
              idProperty = element.property.id ? element.property.id : element.property.objectID
              idBuilding = element.building?.id ? element.building.id : element.building.objectID

              ids = [idProperty, idBuilding]
            } else {
              idProperty = element.property.id ? element.property.id : element.property.objectID

              ids = [idProperty]
            }
            connectionReferences.push({
              id: id,
              relatedIds: ids
            })
          } else if (element.object) {
            const id = element.object.id ? element.object.id : element.object.objectID
            const idProperty = element.property.id ? element.property.id : element.property.objectID
            const idBuilding = element.building.id ? element.building.id : element.building.objectID
            connectionReferences.push({
              id: id,
              relatedIds: [idProperty, idBuilding]
            })
          } else if (element.building) {
            const id = element.building.id ? element.building.id : element.building.objectID
            const idProperty = element.property.id ? element.property.id : element.property.objectID
            connectionReferences.push({
              id: id,
              relatedIds: [idProperty]
            })
          } else if (element.property) {
            const id = element.property.id ? element.property.id : element.property.objectID
            connectionReferences.push({
              id: id,
              relatedIds: []
            })
          }
        })
        this.activity.references = connectionReferences
        this.activity.ownerId = this.accountId
        ContactApi.addActivityV3(this.activity)
          .then(response => {
            this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
            this.hide()
            this.$emit('activity-created', response.activity)
            Vue.toasted.show(this.$t('message.successMessages.activityCreation'), { type: 'success' })
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.activity'), { type: 'error' })
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    deleteMedia(id) {
      this.activity.mediaIds = this.activity.mediaIds.filter(e => e !== id)
      this.mediasList = this.mediasList.filter(el => el.id !== id)
    },
    hide() {
      this.$v.$reset()
      this.$refs['modal-create-activity'].hide()
    },
    show() {
      this.loadInstantSearch()
      this.$refs['modal-create-activity'].show()
    },
    updateActivity() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.loading = true
        const connectionReferences = []
        this.references.forEach(element => {
          if (element.contact) {
            const id = element.contact.id ? element.contact.id : element.contact.objectID
            connectionReferences.push({
              id: id,
              relatedIds: []
            })
          } else if (element.tenancy) {
            const id = element.tenancy.id ? element.tenancy.id : element.tenancy.objectID
            const idProperty = element.property.id ? element.property.id : element.property.objectID
            const idBuilding = element.building.id ? element.building.id : element.building.objectID
            const idObject = element.object.id ? element.object.id : element.object.objectID
            connectionReferences.push({
              id: id,
              relatedIds: [idProperty, idBuilding, idObject]
            })
          } else if (element.request) {
            const id = element.request.id ? element.request.id : element.request.objectID
            connectionReferences.push({
              id: id,
              relatedIds: []
            })
          } else if (element.condominium) {
            const id = element.condominium.id ? element.condominium.id : element.condominium.objectID
            const idProperty = element.property.id ? element.property.id : element.property.objectID
            const idBuilding = element.building.id ? element.building.id : element.building.objectID
            const idObject = element.object.id ? element.object.id : element.object.objectID
            connectionReferences.push({
              id: id,
              relatedIds: [idProperty, idBuilding, idObject]
            })
          } else if (element.device) {
            const id = element.device.id ? element.device.id : element.device.objectID
            let idProperty
            let idBuilding = null
            let idObject = null
            let ids
            if (element.object) {
              idProperty = element.property.id ? element.property.id : element.property.objectID
              idBuilding = element.building?.id ? element.building.id : element.building.objectID
              idObject = element.object?.id ? element.object.id : element.object.objectID

              ids = [idProperty, idBuilding, idObject]
            } else if (element.building) {
              idProperty = element.property.id ? element.property.id : element.property.objectID
              idBuilding = element.building?.id ? element.building.id : element.building.objectID

              ids = [idProperty, idBuilding]
            } else {
              idProperty = element.property.id ? element.property.id : element.property.objectID

              ids = [idProperty]
            }
            connectionReferences.push({
              id: id,
              relatedIds: ids
            })
          } else if (element.object) {
            const id = element.object.id ? element.object.id : element.object.objectID
            const idProperty = element.property.id ? element.property.id : element.property.objectID
            const idBuilding = element.building.id ? element.building.id : element.building.objectID
            connectionReferences.push({
              id: id,
              relatedIds: [idProperty, idBuilding]
            })
          } else if (element.building) {
            const id = element.building.id ? element.building.id : element.building.objectID
            const idProperty = element.property.id ? element.property.id : element.property.objectID
            connectionReferences.push({
              id: id,
              relatedIds: [idProperty]
            })
          } else if (element.property) {
            const id = element.property.id ? element.property.id : element.property.objectID
            connectionReferences.push({
              id: id,
              relatedIds: []
            })
          }
        })
        this.activity.references = connectionReferences
        this.activity.ownerId = this.accountId
        this.activity.employeeContactIds = this.selectedEmployee.map(x => x.objectID)
        ContactApi.updateActivityV3(this.activity)
          .then(() => {
            const updatedActivity = this.activityDetail
              ? Object.assign({}, this.activityDetail, this.activity)
              : Object.assign({}, this.activity)
            this.$emit('activity-updated', updatedActivity, this.currentEmployee)
            this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
            this.hide()
            Vue.toasted.show(this.$t('message.successMessages.activityUpdated'), { type: 'success' })
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.activity'), { type: 'error' })
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    loadCurrentEmployee(employeeContactId) {
      if (employeeContactId !== '' || employeeContactId.length > 0) {
        const contactIds = Array.isArray(employeeContactId) ? employeeContactId : [employeeContactId]
        ContactApi.contactResolve(contactIds)
          .then(response => {
            const contacts = response.persons.concat(response.companies)
              .map(contact => ({ ...contact, objectID: contact.id }))
            this.skipNextEmployeeWatch = true
            this.selectedEmployee = contacts.filter(x => employeeContactId.includes(x.objectID.toString()))
          })
          .catch(e => {
            console.log(e)
          })
      }
      return null
    },
    onBillableChange(checked) {
      this.activity.billable = checked
      if (!checked) {
        this.activity.hourlyRate = null
        this.userOverrodeHourlyRate = false
        return
      }
      this.userOverrodeHourlyRate = false
      this.prefillHourlyRateFromSelectedEmployee()
    },
    prefillHourlyRateFromSelectedEmployee() {
      if (!this.activity.billable || this.userOverrodeHourlyRate) {
        return
      }
      if (this.selectedEmployee.length !== 1) {
        this.activity.hourlyRate = null
        return
      }
      const employeeContact = this.selectedEmployee[0]
      const email = (employeeContact.email || '').toLowerCase().trim()
      if (!email) {
        this.activity.hourlyRate = null
        return
      }
      const employees = this.$store.getters['employee/getEmployees'] || []
      const matchedUser = employees.find(
        employee => employee?.email?.toLowerCase()?.trim() === email
      )
      const rate = matchedUser?.profile?.hourlyRate
      this.activity.hourlyRate = rate != null && rate !== '' ? rate : null
    }
  },
  validations: {
    activity: {
      subject: {
        required
      },
      timestampStart: {
        required
      },
      hourlyRate: {
        required: requiredIf(function () {
          return this.activity.billable
        }),
        positive: function (value) {
          if (!this.activity.billable) {
            return true
          }
          return value != null && value !== '' && Number(value) > 0
        }
      }
    },
    time: {
      start: {
        required: requiredIf(function () {
          return this.activity.billable
        })
      },
      end: {
        required: requiredIf(function () {
          return this.activity.billable
        })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.activity-hourly-rate-placeholder {
  visibility: hidden;
}

.activity-hourly-rate-placeholder-input {
  min-height: calc(1.5em + 0.5rem - 1px);
}

.media-block {
  margin-right: 0.5%;

  .uploaded-file {
    background-position: center center;
    background-size: cover;
    min-height: 7em;
  }

  .file-block {
    border-style: solid;
    border-bottom-color: rgb(206, 212, 218);
    border-width: 0 0 1px 0;

    a {
      overflow-wrap: break-word;
    }
  }
}

.delete-icon {
  position: absolute;
  right: 1rem;
}

.delete-icon :deep(svg) {
  vertical-align: text-top;
}

:deep(.multiselect__single) {
  margin-bottom: 5px;
  margin-top: 2px;
}

:deep(.multiselect__tag) {
  padding: 4px 22px 3px 7px;
  margin-bottom: -1px;
  margin-top: 1px;
}

.showSelected {
  display: none;
}

.selected {
  background-color: rgba(0, 0, 0, 0.08);
}

.close-img {
  width: 15px;
}

.selected .showSelected {
  display: block;
  color: rgba(0, 0, 0, 0.5);
  align-self: center;
  margin-right: 20px;
  font-weight: 600;
}

.color-box.condo_owner {
  background: #f589c7;
}

.color-box.condominium {
  background: #913489;
}

.color-box.tenancy {
  background: #00a4bd;
}

.color-box.object {
  background: #f38d38;
}

.color-box.device {
  background: #767171;
}

.color-box.request {
  background: #f7b6ae;
}

.containerCircles {
  height: 21px;
  align-items: center;
  display: flex;
}

:deep(.autosuggest__results) {
  z-index: 9998 !important;
}

.tooltip {
  z-index: 9999;
}

.autosuggest__results-item .selected {
  color: rgba(0, 0, 0, 0.5) !important;
}

:deep(.tooltip.b-tooltip) {
  z-index: 9999;
}

.selectedOption {
  color: #495057;
  background-color: #e9ecef;
}
</style>
